/* eslint complexity: ["error", {max: 14}] -- v1 + v2 group class endpoints with field-mapping transforms */
/* eslint max-lines: ["error", {max: 625}] -- v1 + v2 group class endpoints with field-mapping transforms */
/* eslint max-lines-per-function: ["error", {max: 275}] -- v1 + v2 group class endpoints with field-mapping transforms */
import isNil from "lodash/isNil";

import api, { CacheIdType, CacheTagType } from "@/store/api";
import type { AvatarCardProps } from "@/ui/core/AvatarCard";
import {
  getGroupClassesOptions,
  onQueryStartedForGroupClassMutation,
} from "@/utils/api/schedule/groupClass.util";
import {
  toGroupClassSeries,
  transformGroupClassesToGroupClassSeries,
} from "@/utils/groupClass.util";
import { getPreferredFullName } from "@/utils/user.util";

interface RecommendedGroupClassInstance {
  endTime: string;
  groupClassExternalId: string;
  // v2 registration is per group section; undefined for the v1 flow
  groupSectionId?: string;
  startTime: string;
}

interface RecommendedGroupClass {
  description: string;
  // v2 recommended groups carry these so the modal can fetch group details + show the Core
  // tag; undefined for the v1 recommended-group-class flow
  groupId?: string;
  iconName: string;
  instances: RecommendedGroupClassInstance[];
  isCore?: boolean;
  lengthInWeeks: number;
  title: string;
}

interface GetRecommendedGroupClassApiData {
  recommendedGroupClass?: null | RecommendedGroupClass;
}

interface GetRecommendedGroupClassApiResponse {
  data: GetRecommendedGroupClassApiData;
  status: string;
}

// Jackson strips the `is` prefix from booleans, so `boolean isCore` etc. arrive in the
// API response as `core`/`sensitive`/`userRegistered`; these interfaces mirror the API response names.
interface RecommendedGroupInstanceResponse {
  endTime: string;
  groupSectionEventId: string;
  groupSectionId: string;
  startTime: string;
}

interface RecommendedGroupResponseItem {
  description?: null | string;
  groupId: string;
  groupTypeCode: string;
  iconName?: null | string;
  instances: RecommendedGroupInstanceResponse[];
  // this endpoint sends core/sensitive already `is`-prefixed (unlike other group
  // endpoints where Jackson strips the prefix)
  isCore: boolean;
  // group-level registration flag (replaces the former per-instance `userRegistered`)
  isRegistered: boolean;
  isSensitive: boolean;
  lengthInWeeks?: null | number;
  title: string;
}

// the recommended-groups endpoint returns a single group object (or nothing) rather than
// a list; we normalize it to RecommendedGroup[] in the transform so downstream (the queue
// builder, which iterates groups) stays unchanged
type GetRecommendedGroupsResponse = null | RecommendedGroupResponseItem;

interface GetRecommendedGroupsRequest {
  patientExternalId: string;
}

interface RegisteredGroupResponseItem {
  description?: null | string;
  duration?: null | number;
  endDate?: null | string;
  frequencyCode?: null | string;
  groupActiveSectionCount?: null | number;
  groupId: string;
  groupSectionId: string;
  groupTypeCode?: null | string;
  hostUserIds?: null | string[];
  iconName?: null | string;
  // this endpoint sends core/sensitive already `is`-prefixed (unlike other group
  // endpoints where Jackson strips the prefix)
  isCore?: boolean;
  isSensitive?: boolean;
  nextEventStartTime?: null | string;
  registeredUserIds?: null | string[];
  sessionCode?: null | string;
  startDate?: null | string;
  title: string;
}

type GetMyRegisteredGroupsResponse = RegisteredGroupResponseItem[];

interface GetMyRegisteredGroupsRequest {
  patientExternalId?: string;
}

interface AvailableGroupResponseItem {
  description?: null | string;
  groupActiveSectionCount: number;
  groupTypeCode?: null | string;
  iconName?: null | string;
  id: string;
  // this endpoint sends core/sensitive already `is`-prefixed (unlike other group
  // endpoints where Jackson strips the prefix); userRegistered stays unprefixed
  isCore: boolean;
  isSensitive: boolean;
  title: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention -- matches API response name
  userRegistered: boolean;
}

// the v2 groups endpoint wraps the list in a paginated envelope
interface GetGroupsResponse {
  count: number;
  data: AvailableGroupResponseItem[];
  pageNumber: number;
  pageSize: number;
}

interface GetGroupsRequest {
  patientExternalId?: string;
}

interface GroupSectionHostResponse {
  externalId: string;
  firstName: string;
  lastName: string;
  pronouns?: null | string[];
  providerType?: null | string;
  type: string;
}

interface GroupSectionResponseItem {
  duration?: null | number;
  endDate?: null | string;
  frequency?: null | string;
  hosts?: GroupSectionHostResponse[] | null;
  id: string;
  nextEventStartTime?: string;
  sessionCode?: null | string;
  startDate?: null | string;
  // eslint-disable-next-line @typescript-eslint/naming-convention -- matches API response name
  userRegistered: boolean;
}

interface GetGroupDetailsResponse {
  // eslint-disable-next-line @typescript-eslint/naming-convention -- matches API response name
  core: boolean;
  description?: null | string;
  iconName?: null | string;
  id: string;
  sections?: GroupSectionResponseItem[] | null;
  // eslint-disable-next-line @typescript-eslint/naming-convention -- matches API response name
  sensitive: boolean;
  title: string;
}

interface GetGroupDetailsRequest {
  groupId: string;
  patientExternalId?: string;
}

interface GetGroupClassRequest {
  groupClassExternalId: string;
  patientId?: string;
}

interface GetGroupClassesRequest {
  patientId?: string;
}

interface GCRegistrationRequest {
  groupClassExternalId: string;
  patientExternalId?: string;
  source?: string;
}

interface GCRegistrationResponse {
  data?: string;
  status?: string;
}

interface UpdateGroupSectionRegistrationRequest {
  groupSectionId: string;
  isRegistered: boolean;
  patientExternalId: string;
  source?: string;
}

interface UpdateGroupSectionRegistrationResponse {
  groupSectionId?: string;
  isRegistered?: boolean;
  isReserved?: boolean;
  userExternalId?: string;
}

interface DeclineGroupSectionEventRequest {
  groupSectionEventId: string;
  patientExternalId?: string;
}

interface DeclineGroupSectionEventResponse {
  message?: string;
}

const toGroupSectionAttendee = (
  host: GroupSectionHostResponse,
): AvatarCardProps => ({
  pronouns: host.pronouns ?? undefined,
  subtitle: host.providerType ?? undefined,
  userId: host.externalId,
  userName: getPreferredFullName({
    firstName: host.firstName,
    lastName: host.lastName,
  }),
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- API type string → BaseUserType
  userType: host.type as BaseUserType,
});

const GetGroupClassEndpoint = "getGroupClass";
const GetGroupClassesEndpoint = "getGroupClasses";
const GetRecommendedGroupClassEndpoint = "getRecommendedGroupClass";
const GetRecommendedGroupsEndpoint = "getRecommendedGroups";
const GetMyRegisteredGroupsEndpoint = "getMyRegisteredGroups";
const GetGroupsEndpoint = "getGroups";
const GetGroupDetailsEndpoint = "getGroupDetails";

const ApiWithGroupClassQueries = api.injectEndpoints({
  endpoints: (build) => ({
    [GetGroupClassEndpoint]: build.query<
      GroupClassSeries | undefined,
      GetGroupClassRequest
    >({
      // eslint-disable-next-line max-params
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        const { patientId } = arg;
        const { data: retrievedGroupClass } = await queryFulfilled;
        if (!retrievedGroupClass) return;

        dispatch(
          ApiWithGroupClassQueries.util.updateQueryData(
            GetGroupClassesEndpoint,
            { patientId },
            (cacheData: GroupClassSeries[]) => {
              const oldGroupClass = cacheData.find(
                (gc: GroupClassSeries) =>
                  gc.groupClassExternalId ===
                  retrievedGroupClass.groupClassExternalId,
              );
              if (!oldGroupClass) return;
              Object.assign(oldGroupClass, retrievedGroupClass);
            },
          ),
        );
      },
      query: ({ groupClassExternalId, patientId }) => ({
        params: {
          ...(patientId && { "patient-external-id": patientId }),
        },
        url: `/schedule/family/group-classes/${groupClassExternalId}`,
      }),

      transformResponse: (
        response: GetGroupClassResponse,
      ): GroupClassSeries | undefined => {
        if (isNil(response) || !response.data) return undefined;
        return toGroupClassSeries(response.data);
      },
    }),
    [GetGroupClassesEndpoint]: build.query<
      GroupClassSeries[],
      GetGroupClassesRequest
    >({
      query: ({ patientId }) => getGroupClassesOptions(patientId),
      /* eslint-disable-next-line max-params */
      transformResponse: (response: GetGroupClassesResponse, _meta, arg) => {
        const groupClasses =
          isNil(response) || isNil(response.data.data)
            ? []
            : response.data.data;
        return transformGroupClassesToGroupClassSeries({
          groupClasses,
          patientId: arg.patientId,
        });
      },
    }),
    [GetGroupDetailsEndpoint]: build.query<
      GroupDetails | undefined,
      GetGroupDetailsRequest
    >({
      // eslint-disable-next-line max-params
      providesTags: (_result, _error, arg) => [
        {
          id: arg.patientExternalId || CacheIdType.AppUser,
          type: CacheTagType.GroupClassEvents,
        },
      ],
      query: ({ groupId, patientExternalId }) => ({
        params: {
          ...(patientExternalId && {
            "patient-external-id": patientExternalId,
          }),
        },
        url: `/schedule/family/v2/groups/${groupId}`,
      }),
      transformResponse: (
        response: GetGroupDetailsResponse | undefined,
      ): GroupDetails | undefined => {
        if (isNil(response)) return undefined;
        return {
          description: response.description ?? undefined,
          groupId: response.id,
          iconName: response.iconName ?? undefined,
          isCore: response.core,
          isSensitive: response.sensitive,
          sections: (response.sections ?? []).map((section) => ({
            duration: section.duration ?? undefined,
            endDate: section.endDate ?? undefined,
            frequency: section.frequency ?? undefined,
            groupSectionId: section.id,
            hosts: (section.hosts ?? []).map(toGroupSectionAttendee),
            isUserRegistered: section.userRegistered,
            nextEventStartTime: section.nextEventStartTime ?? undefined,
            sessionCode: section.sessionCode ?? undefined,
            startDate: section.startDate ?? undefined,
          })),
          title: response.title,
        };
      },
    }),
    [GetGroupsEndpoint]: build.query<AvailableGroup[], GetGroupsRequest>({
      // eslint-disable-next-line max-params
      providesTags: (_result, _error, arg) => [
        {
          id: arg.patientExternalId || CacheIdType.AppUser,
          type: CacheTagType.GroupClassEvents,
        },
      ],
      query: ({ patientExternalId }) => ({
        params: {
          // mirror v1: fetch a single large page rather than paginate, so client-side
          // Fuse search covers the full list
          "page-size": 200,
          ...(patientExternalId && {
            "patient-external-id": patientExternalId,
          }),
        },
        url: "/schedule/family/v2/groups",
      }),
      transformResponse: (
        response: GetGroupsResponse | undefined,
      ): AvailableGroup[] => {
        const groups = response?.data;
        if (isNil(groups)) return [];
        return groups.map((group) => ({
          description: group.description ?? undefined,
          groupId: group.id,
          groupTypeCode: group.groupTypeCode ?? undefined,
          iconName: group.iconName ?? undefined,
          isCore: group.isCore,
          isSensitive: group.isSensitive,
          isUserRegistered: group.userRegistered,
          numberOfSections: group.groupActiveSectionCount,
          title: group.title,
        }));
      },
    }),
    [GetMyRegisteredGroupsEndpoint]: build.query<
      RegisteredGroup[],
      GetMyRegisteredGroupsRequest
    >({
      // eslint-disable-next-line max-params
      providesTags: (_result, _error, arg) => [
        {
          id: arg.patientExternalId || CacheIdType.AppUser,
          type: CacheTagType.GroupClassEvents,
        },
      ],
      query: ({ patientExternalId }) => ({
        params: {
          ...(patientExternalId && {
            "patient-external-id": patientExternalId,
          }),
        },
        url: "/schedule/family/v2/my-groups",
      }),
      transformResponse: (
        response: GetMyRegisteredGroupsResponse | undefined,
      ): RegisteredGroup[] => {
        if (isNil(response)) return [];
        // a user can be registered in multiple sections of the same group, so each row is
        // kept as-is (no dedupe by groupId)
        return response.map(
          // eslint-disable-next-line no-restricted-syntax -- full-field mapping of the RegisteredGroup domain type
          (group) => ({
            description: group.description ?? undefined,
            duration: group.duration ?? undefined,
            endDate: group.endDate ?? undefined,
            frequencyCode: group.frequencyCode ?? undefined,
            groupId: group.groupId,
            groupSectionId: group.groupSectionId,
            groupTypeCode: group.groupTypeCode ?? undefined,
            hostUserIds: group.hostUserIds ?? [],
            iconName: group.iconName ?? undefined,
            isCore: group.isCore ?? undefined,
            isSensitive: group.isSensitive ?? undefined,
            nextEventStartTime: group.nextEventStartTime ?? undefined,
            registeredUserIds: group.registeredUserIds ?? [],
            sessionCode: group.sessionCode ?? undefined,
            startDate: group.startDate ?? undefined,
            title: group.title,
          }),
        );
      },
    }),
    [GetRecommendedGroupClassEndpoint]: build.query<
      RecommendedGroupClass | undefined,
      { patientExternalId: string }
    >({
      query: ({ patientExternalId }) => ({
        url: `/schedule/family/patients/${patientExternalId}/recommended-group-classes`,
      }),
      transformResponse: (
        response: GetRecommendedGroupClassApiResponse | undefined,
      ): RecommendedGroupClass | undefined => {
        if (isNil(response) || isNil(response.data)) return undefined;
        const { recommendedGroupClass } = response.data;
        if (isNil(recommendedGroupClass)) return undefined;
        return recommendedGroupClass;
      },
    }),
    [GetRecommendedGroupsEndpoint]: build.query<
      RecommendedGroup[],
      GetRecommendedGroupsRequest
    >({
      // eslint-disable-next-line max-params
      providesTags: (_result, _error, arg) => [
        {
          id: arg.patientExternalId || CacheIdType.AppUser,
          type: CacheTagType.GroupClassEvents,
        },
      ],
      query: ({ patientExternalId }) => {
        const url = `/schedule/family/v2/patients/${patientExternalId}/recommended-groups`;
        return { url };
      },
      transformResponse: (
        response: GetRecommendedGroupsResponse | undefined,
      ): RecommendedGroup[] => {
        if (isNil(response)) return [];
        return [
          {
            description: response.description ?? undefined,
            groupId: response.groupId,
            groupTypeCode: response.groupTypeCode,
            iconName: response.iconName ?? undefined,
            instances: response.instances.map((instance) => ({
              endTime: instance.endTime,
              groupSectionEventId: instance.groupSectionEventId,
              groupSectionId: instance.groupSectionId,
              startTime: instance.startTime,
            })),
            isCore: response.isCore,
            isRegistered: response.isRegistered,
            isSensitive: response.isSensitive,
            lengthInWeeks: response.lengthInWeeks ?? undefined,
            title: response.title,
          },
        ];
      },
    }),
  }),
});

// since some mutations invoke some of these queries in their own onQueryStarte,d
// we inject mutations after having injected the queries, to ensure the queries are initialized
const ApiWithGroupClassQueriesAndMutations =
  ApiWithGroupClassQueries.injectEndpoints({
    endpoints: (build) => ({
      declineGroupSectionEvent: build.mutation<
        DeclineGroupSectionEventResponse,
        DeclineGroupSectionEventRequest
      >({
        // invalidate the specific patient (when known) and always the app-user tag, so
        // both self and proxy schedules refresh. Falling back to AppUser avoids emitting
        // an { id: undefined } tag that would match nothing.
        // eslint-disable-next-line max-params
        invalidatesTags: (_result, _error, arg) => [
          {
            id: arg.patientExternalId || CacheIdType.AppUser,
            type: CacheTagType.GroupClassEvents,
          },
          { id: CacheIdType.AppUser, type: CacheTagType.GroupClassEvents },
        ],
        query: ({ groupSectionEventId, patientExternalId }) => ({
          body: patientExternalId ? { patientExternalId } : {},
          method: "PATCH",
          url: `/schedule/family/v2/group-section-events/${groupSectionEventId}/decline`,
        }),
      }),
      registerForGroupClass: build.mutation<
        GCRegistrationResponse,
        GCRegistrationRequest
      >({
        // eslint-disable-next-line max-params
        invalidatesTags: (_result, _error, arg) => {
          const tag = arg.patientExternalId || CacheIdType.AppUser;
          return [{ id: tag, type: CacheTagType.GroupClassEvents }];
        },
        // when registerForGroupClass succeeds, update the cached result from getGroupClasses
        onQueryStarted: onQueryStartedForGroupClassMutation({
          getGroupClassEndpoint: GetGroupClassEndpoint,
          scheduleApi: ApiWithGroupClassQueries,
        }),
        query: ({ groupClassExternalId, patientExternalId, source }) => ({
          body: {
            ...(patientExternalId ? { patientExternalId } : {}),
            ...(source ? { source } : {}),
          },
          method: "POST",
          url: `/schedule/family/group-classes/${groupClassExternalId}/register`,
        }),
      }),
      unregisterForGroupClass: build.mutation<
        GCRegistrationResponse,
        GCRegistrationRequest
      >({
        // eslint-disable-next-line max-params
        invalidatesTags: (_result, _error, arg) => {
          const tag = arg.patientExternalId || CacheIdType.AppUser;
          return [{ id: tag, type: CacheTagType.GroupClassEvents }];
        },
        // when unregisterForGroupClass succeeds, update the cached result from getGroupClasses
        onQueryStarted: onQueryStartedForGroupClassMutation({
          getGroupClassEndpoint: GetGroupClassEndpoint,
          scheduleApi: ApiWithGroupClassQueries,
        }),
        query: ({ groupClassExternalId, patientExternalId }) => ({
          body: patientExternalId ? { patientExternalId } : {},
          method: "POST",
          url: `/schedule/family/group-classes/${groupClassExternalId}/unregister`,
        }),
      }),
      updateGroupSectionRegistration: build.mutation<
        UpdateGroupSectionRegistrationResponse,
        UpdateGroupSectionRegistrationRequest
      >({
        // invalidate both the specific patient id and the app-user tag: list queries key
        // "self" on CacheIdType.AppUser (patientId maps self → undefined) while this
        // mutation always carries the concrete patient id, so cover both to match either.
        // eslint-disable-next-line max-params
        invalidatesTags: (_result, _error, arg) => [
          {
            id: arg.patientExternalId || CacheIdType.AppUser,
            type: CacheTagType.GroupClassEvents,
          },
          { id: CacheIdType.AppUser, type: CacheTagType.GroupClassEvents },
        ],
        query: ({
          groupSectionId,
          isRegistered,
          patientExternalId,
          source,
        }) => {
          const body = {
            isRegistered,
            ...(source ? { source } : {}),
            userExternalId: patientExternalId,
          };
          const url = `/schedule/family/v2/patients/${patientExternalId}/group-section-registrations/${groupSectionId}`;
          return { body, method: "PATCH", url };
        },
      }),
    }),
  });

const groupClassApi = ApiWithGroupClassQueriesAndMutations;

const {
  useDeclineGroupSectionEventMutation,
  useGetGroupClassesQuery,
  useGetGroupDetailsQuery,
  useGetGroupsQuery,
  useGetMyRegisteredGroupsQuery,
  useGetRecommendedGroupClassQuery,
  useLazyGetGroupClassesQuery,
  useLazyGetRecommendedGroupClassQuery,
  useLazyGetRecommendedGroupsQuery,
  useRegisterForGroupClassMutation,
  useUnregisterForGroupClassMutation,
  useUpdateGroupSectionRegistrationMutation,
} = groupClassApi;

export {
  useDeclineGroupSectionEventMutation,
  useGetGroupClassesQuery,
  useGetGroupDetailsQuery,
  useGetGroupsQuery,
  useGetMyRegisteredGroupsQuery,
  useGetRecommendedGroupClassQuery,
  useLazyGetGroupClassesQuery,
  useLazyGetRecommendedGroupClassQuery,
  useLazyGetRecommendedGroupsQuery,
  useRegisterForGroupClassMutation,
  useUnregisterForGroupClassMutation,
  useUpdateGroupSectionRegistrationMutation,
};

export type { RecommendedGroupClass, RecommendedGroupClassInstance };
